import { ProductDetail } from "./ProductDetail";
import type { EstimateLineItem } from "@/hooks/useEstimateLineItems";
import type { InstallationPhase } from "@/utils/installationPhases";
import * as Icons from "lucide-react";

interface TierOption {
  tier: 'good' | 'better' | 'best';
  product_id: string;
  product?: {
    name: string;
    brand?: string;
  };
  priceDifference?: number;
}

interface PhaseSectionProps {
  phase: InstallationPhase;
  lineItems: EstimateLineItem[];
  showImages: boolean;
  showTierSelector?: boolean;
  tierDefaults?: any[];
  onTierChange?: (tier: 'good' | 'better' | 'best', productId: string) => void;
}

export function PhaseSection({ 
  phase, 
  lineItems, 
  showImages,
  showTierSelector = false,
  tierDefaults = [],
  onTierChange
}: PhaseSectionProps) {
  // Get icon component from lucide-react based on string name
  const IconComponent = (Icons as any)[phase.icon] || Icons.Circle;

  // Detect current tier from line items by matching against tier defaults
  const getCurrentTier = (lineItem: EstimateLineItem): 'good' | 'better' | 'best' => {
    // Find which tier this product is assigned to in the phase tier defaults
    const matchedTier = tierDefaults.find(td => td.product_id === lineItem.product_id);
    return (matchedTier?.tier as 'good' | 'better' | 'best') || 'good';
  };

  return (
    <section className="mb-12 page-break-inside-avoid">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <IconComponent className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-foreground">
            {phase.number}. {phase.title}
          </h3>
          <p className="text-muted-foreground">{phase.customerExplanation}</p>
        </div>
      </div>

      <div className="space-y-6 ml-16">
        {lineItems.map(item => {
          // Check if this product is part of the tier group for this phase
          const isPartOfTierGroup = tierDefaults.some(td => td.product_id === item.product_id);
          
          return (
            <ProductDetail
              key={item.id}
              lineItem={item}
              showImage={showImages}
              showTierSelector={showTierSelector && isPartOfTierGroup}
              availableTiers={isPartOfTierGroup ? tierDefaults.map(td => {
                const currentPrice = item.line_total;
                const tierPrice = td.unit_price ? td.unit_price * item.quantity : 0;
                const priceDifference = tierPrice - currentPrice;
                
                return {
                  tier: td.tier,
                  product_id: td.product_id,
                  product: td.product,
                  priceDifference: priceDifference
                };
              }) : []}
              currentTier={getCurrentTier(item)}
              onTierChange={onTierChange}
            />
          );
        })}
      </div>
    </section>
  );
}
